import { assert } from "../../shared/Utils.js";
import EventTarget from "@ungap/event-target";
const DefaultEsPoolOptions = {
    size: navigator.hardwareConcurrency,
    name: "EsThreadPool",
};
export class EsThreadPool extends EventTarget {
    threads = [];
    terminateThread;
    options;
    constructor(poolOptions, terminateThread) {
        super();
        this.options = { ...DefaultEsPoolOptions, ...poolOptions };
        this.terminateThread = terminateThread;
    }
    static async Spawn(spawnThread, poolOptions = {}, initialiseThread, terminateThread) {
        const pool = new EsThreadPool(poolOptions, terminateThread);
        const doSpawn = async (idx) => {
            const thread = await spawnThread(idx);
            if (initialiseThread) {
                try {
                    await initialiseThread(idx, thread);
                }
                catch (e) {
                    await thread.terminate();
                    throw e;
                }
            }
            thread.addEventListener("error", pool.handleErrorEvent);
            return thread;
        };
        const threadSpawnPromises = [];
        for (let idx = 0; idx < pool.options.size; ++idx) {
            threadSpawnPromises.push(doSpawn(idx));
        }
        try {
            pool.threads.push(...await Promise.all(threadSpawnPromises));
            return pool;
        }
        catch (e) {
            const res = await Promise.allSettled(threadSpawnPromises);
            const terminatePromises = [];
            const rejectionMessages = [];
            for (const [idx, item] of res.entries()) {
                if (item.status === "fulfilled") {
                    terminatePromises.push(pool.doTerminate(idx, item.value));
                }
                if (item.status === "rejected")
                    rejectionMessages.push(`\tThread #${idx}: ${item.reason.toString()}`);
            }
            await Promise.all(terminatePromises);
            throw new Error("Failed to spawn thread pool. Errors:\n" + rejectionMessages.join("\n"));
        }
    }
    handleErrorEvent = ((evt) => {
        assert(evt instanceof ErrorEvent, "Unhandled event type");
        this.dispatchEvent(new ErrorEvent("error", { error: evt.error }));
    }).bind(this);
    async queue(taskFunction) {
        return taskFunction(this.threads[this.findThreadWithFewTasks()]);
    }
    findThreadWithFewTasks() {
        let min = Infinity;
        let threadId = 0;
        for (const [idx, thread] of this.threads.entries()) {
            if (thread.numQueuedTasks < min) {
                threadId = idx;
                min = thread.numQueuedTasks;
            }
        }
        return threadId;
    }
    async settled() {
        const settledThreads = [];
        for (const thread of this.threads) {
            settledThreads.push(thread.settled());
        }
        await Promise.allSettled(settledThreads);
    }
    async resolved() {
        const settledThreads = [];
        for (const thread of this.threads) {
            settledThreads.push(thread.resolved());
        }
        await Promise.all(settledThreads);
    }
    async doTerminate(threadId, thread, forceTerminateShared) {
        if (this.terminateThread)
            await this.terminateThread(threadId, thread);
        thread.removeEventListener("error", this.handleErrorEvent);
        await thread.terminate(forceTerminateShared);
    }
    async terminate(forceTerminateShared) {
        const terminatePromises = [];
        for (const [idx, thread] of this.threads.entries()) {
            terminatePromises.push(this.doTerminate(idx, thread, forceTerminateShared));
        }
        await Promise.all(terminatePromises);
    }
}
